import type { RoutePattern } from '@/services/router/match-route';
import { matchRoutes } from '@/services/router/match-route';

/** Context the agent's static-suggestion exclusion predicates can read. */
interface SuggestionContext {
  isBuilder: boolean;
}

interface Suggestion {
  label: string;
  message: string;
  /**
   * Hard exclude predicate. Use for plan gating or any other "never show this"
   * condition. Filtered out before the suggestion enters the marquee or the
   * next-message pool.
   */
  excludeIf?: (context: SuggestionContext) => boolean;
}

/** Convenience predicate for `excludeIf` on suggestions that depend on Builder-restricted features. */
const isPlanBuilder = (context: SuggestionContext): boolean => context.isBuilder;

/**
 * Static list of generic suggestions shown when no route-specific suggestions apply.
 */
const STATIC_SUGGESTIONS: Suggestion[] = [
  {
    label: 'Create a Campaign',
    message:
      "I'd like to create a campaign. Can you ask me a few questions to understand my goal, audience, and channel before we get started?",
    excludeIf: isPlanBuilder,
  },
  {
    label: 'What are triggers?',
    message: 'What are campaign triggers and how do they work?',
    excludeIf: isPlanBuilder,
  },
  {
    label: 'Build a Segment',
    message: 'I want to build a segment. Can you ask me questions to help define the right audience criteria?',
  },
  { label: 'What is a journey?', message: 'What is a customer journey in Customer.io?' },
  {
    label: 'Draft a Broadcast',
    message:
      'I want to draft a broadcast. Can you ask me about my audience, goal, and message before writing anything?',
  },
  { label: 'How do I track events?', message: 'How do I track custom events in Customer.io?' },
  {
    label: 'Brainstorm a Campaign',
    message:
      'I want to brainstorm a new campaign idea. Can you ask me about my goals and audience to help generate the right concepts?',
    excludeIf: isPlanBuilder,
  },
  { label: 'What are webhooks?', message: 'What are webhooks and how do I configure them?' },
  {
    label: 'Create a Welcome Series',
    message:
      "I'd like to create a welcome series for new users. What questions do you have to help me design the right flow?",
    excludeIf: isPlanBuilder,
  },
  { label: 'What is personalization?', message: 'What is message personalization and how do I use it?' },
  {
    label: 'Find inactive users',
    message:
      "I want to find inactive users in my workspace. Can you ask me what criteria or timeframe I'm thinking about?",
  },
  { label: 'What is A/B testing?', message: 'What is A/B testing and how do I set it up?' },
  {
    label: 'Write a re-engagement email',
    message:
      "I want to write a re-engagement email. Can you ask me about my audience and what I'm trying to win back before drafting anything?",
    excludeIf: isPlanBuilder,
  },
  { label: 'What are transactional emails?', message: 'What are transactional emails and how do I send them?' },
  {
    label: 'Create a Routine',
    message: 'I want to create a routine. What do you need to know about my use case to help me build the right one?',
  },
  { label: 'What integrations exist?', message: 'What third-party tools can I integrate with Customer.io?' },
  {
    label: 'Analyze campaign performance',
    message:
      "I'd like to analyze the performance of a campaign. What information do you need from me to help with this?",
    excludeIf: isPlanBuilder,
  },
  { label: 'What new features launched?', message: 'What new features has Customer.io launched recently?' },
  {
    label: 'Create a retention goal',
    message:
      'I want to create a retention goal. What do you need to know about my product and users to help me set this up?',
    excludeIf: isPlanBuilder,
  },
  {
    label: 'Contact support',
    message:
      'I need to contact Customer.io support. Can you help me figure out the best way to reach them or describe my issue?',
  },
];

interface PageSuggestionGroup {
  routes: RoutePattern[];
  suggestions: Suggestion[];
}

/**
 * Route-specific suggestions matched by Route patterns.
 *
 * Routes ending with `/*` use prefix matching (any sub-path matches).
 * All other routes use exact matching.
 * Groups are checked in order — the first match wins.
 *
 * Only includes suggestions that are generic enough to work without workspace-specific state.
 */
const PAGE_SUGGESTIONS: PageSuggestionGroup[] = [
  // ── Dashboard / Home ──────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/dashboard', '/workspaces/:workspaceId/journeys/home/*'],
    suggestions: [
      {
        label: 'Plan a messaging calendar',
        message: 'Help me plan a 30-day messaging calendar based on best practices for customer engagement',
      },
      {
        label: 'Untargeted segments?',
        message: 'How can I find audience segments that are not being targeted by any active campaign?',
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Gap analysis',
        message: "What's the next campaign I should build to fill a gap in my customer journey?",
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Re-engage inactive users',
        message: 'What type of campaign is most effective to re-engage inactive users?',
        excludeIf: isPlanBuilder,
      },
      { label: 'Measure weekly performance', message: 'How do I set up a weekly summary of my messaging performance?' },
    ],
  },

  // ── Campaign — Workflow Builder ───────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/campaigns/:campaignId/setup/workflow/actions',
      '/workspaces/:workspaceId/journeys/campaigns/:campaignId/workflow',
      '/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/workflow/actions',
    ],
    suggestions: [
      {
        label: 'Review my workflow',
        message: 'What are common logical gaps or missing steps to look for when reviewing a campaign workflow?',
      },
      { label: 'Ideal email delay', message: "What's the ideal delay between emails in an onboarding sequence?" },
      {
        label: 'Branch on click',
        message: 'How do I add a branch that sends different messages based on whether someone clicked?',
      },
      {
        label: 'Write a subject line',
        message: 'Write a compelling subject line and preview text for an onboarding email',
      },
      {
        label: 'Prevent re-entry',
        message: "How do I stop someone from re-entering a campaign if they've already finished it?",
      },
    ],
  },

  // ── Campaigns — List / New / Welcome (before detail — /new matches :campaignId)
  {
    routes: [
      '/workspaces/:workspaceId/journeys/campaigns',
      '/workspaces/:workspaceId/journeys/campaigns/new',
      '/workspaces/:workspaceId/journeys/campaigns/welcome/*',
    ],
    suggestions: [
      {
        label: 'Re-engage inactive users',
        message: 'What type of campaign is most effective to re-engage inactive users?',
      },
      { label: 'Onboarding sequence', message: "What's the ideal campaign sequence for a new user's first 60 days?" },
      { label: 'Event-triggered campaigns', message: 'How do I set up event-triggered campaigns in Customer.io?' },
      {
        label: 'Clean up old drafts',
        message: 'How do I find and clean up campaigns that have been in draft for a long time?',
      },
      {
        label: 'Campaign best practices',
        message: 'What are the best practices for organizing and naming campaigns in Customer.io?',
      },
    ],
  },

  // ── Campaign — Detail / Overview ──────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/campaigns/:campaignId/*'],
    suggestions: [
      {
        label: 'Improve conversions',
        message: 'What are 3 specific changes I could make to improve conversions on a campaign?',
      },
      {
        label: 'Diagnose open rate drop',
        message: 'What are common reasons for an email open rate drop and how do I fix it?',
      },
      {
        label: 'Reduce drop-off',
        message: 'How do I identify which message in a workflow is causing the most drop-off?',
      },
      { label: 'A/B test ideas', message: 'Help me design an A/B test hypothesis for improving a campaign workflow' },
      {
        label: 'Add a conversion goal',
        message: 'What goal or conversion event should I add to make my campaign smarter?',
      },
    ],
  },

  // ── Broadcast / Newsletter — Detail ───────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/*',
      '/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId/*',
    ],
    suggestions: [
      {
        label: 'Follow-up message',
        message: 'Draft a follow-up message for people who opened a broadcast but did not click',
      },
      { label: 'Subject line variants', message: 'Suggest 3 subject line variants I could test in my next broadcast' },
      { label: 'Compare performance', message: "How do I compare a broadcast's performance to my previous sends?" },
      {
        label: 'Improve next send',
        message: 'What would I change about a broadcast to improve results on the next send?',
      },
      {
        label: 'Segment targeting',
        message: 'How do I determine which audience segments respond best to a broadcast?',
      },
    ],
  },

  // ── Broadcasts — List ─────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/broadcasts/*'],
    suggestions: [
      {
        label: 'Best send time',
        message: "What's the best day and time to send a broadcast based on general engagement patterns?",
      },
      {
        label: 'Re-engagement broadcast',
        message: 'Help me plan a re-engagement broadcast — who should I target and what should I say?',
      },
      { label: 'Broadcast calendar', message: 'Help me plan a broadcast calendar for the next 8 weeks' },
      {
        label: 'Broadcast vs campaign',
        message: 'When should I use a broadcast vs a triggered campaign?',
        excludeIf: isPlanBuilder,
      },
      { label: 'Newsletter best practices', message: 'What are best practices for newsletters in Customer.io?' },
    ],
  },

  // ── Segments — List / New / Explore (before detail — /new matches :segmentId)
  {
    routes: [
      '/workspaces/:workspaceId/journeys/segments',
      '/workspaces/:workspaceId/journeys/segments/new',
      '/workspaces/:workspaceId/journeys/segments/explore/*',
    ],
    suggestions: [
      {
        label: 'Consolidate segments',
        message: 'How can I find segments that overlap significantly and could be consolidated?',
      },
      {
        label: 'Build a churn segment',
        message: 'Build me a segment of users who signed up 30+ days ago but have never converted',
      },
      {
        label: 'Launch segmentation',
        message: 'Help me design a segmentation strategy for an upcoming product launch',
      },
      {
        label: 'Unused segments',
        message: 'How do I find segments that are not being used in any active campaign?',
        excludeIf: isPlanBuilder,
      },
      { label: 'Segment best practices', message: 'What are best practices for organizing and maintaining segments?' },
    ],
  },

  // ── Segment — Detail ──────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/segments/:segmentId/*'],
    suggestions: [
      { label: 'Tighten conditions', message: 'How can I tighten segment conditions to make it more precise?' },
      {
        label: 'Draft a targeted email',
        message: 'Draft an email that would resonate specifically with a highly engaged audience segment',
      },
      {
        label: 'Best campaign for segment',
        message: "What's the best campaign type I could run against a specific segment?",
        excludeIf: isPlanBuilder,
      },
      { label: 'Segment too small?', message: 'What are common reasons a segment might be smaller than expected?' },
      {
        label: 'Campaigns using segment',
        message: 'How do I find which campaigns are currently using a specific segment?',
        excludeIf: isPlanBuilder,
      },
    ],
  },

  // ── People — List / New / Merge (before detail — /new matches :personId)
  {
    routes: [
      '/workspaces/:workspaceId/journeys/people',
      '/workspaces/:workspaceId/journeys/people/new',
      '/workspaces/:workspaceId/journeys/people/create',
      '/workspaces/:workspaceId/journeys/people/merge',
      '/workspaces/:workspaceId/journeys/people/welcome',
    ],
    suggestions: [
      {
        label: 'Find unconverted users',
        message: 'How do I find users who signed up a long time ago but have never converted?',
      },
      { label: 'Expiring subscriptions', message: 'How do I find users whose subscription or trial expires soon?' },
      {
        label: 'Unsubscribe patterns',
        message: 'How can I identify patterns among customers who have recently unsubscribed?',
      },
      { label: 'Incomplete profiles', message: 'How do I find users with incomplete profiles missing key attributes?' },
      { label: 'Import people', message: 'How do I import people into Customer.io?' },
    ],
  },

  // ── Person — Detail ───────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/people/:personId/*'],
    suggestions: [
      { label: 'Activity summary', message: "How do I interpret a person's activity timeline in Customer.io?" },
      {
        label: 'Campaign engagement',
        message: 'How can I see which campaigns a specific person has received and engaged with?',
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Active workflows',
        message: 'How do I check if a person is currently inside any active campaign workflows?',
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Re-engage this user',
        message: "What's the best approach to re-engage a user based on their behavior history?",
      },
      {
        label: 'Why did they unsubscribe?',
        message: "What can a person's history tell me about why they might have unsubscribed?",
      },
    ],
  },

  // ── Content / Templates ───────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/message-library',
      '/workspaces/:workspaceId/assets',
      '/workspaces/:workspaceId/journeys/layouts/*',
      '/workspaces/:workspaceId/snippets/*',
      '/workspaces/:workspaceId/journeys/template/:templateId',
    ],
    suggestions: [
      {
        label: 'Archive unused templates',
        message: 'How can I find templates that are not used in any active campaign?',
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Win-back email series',
        message: 'Create a template outline for a 3-part win-back email series',
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Template consistency',
        message: 'What are best practices for keeping email templates consistent across campaigns?',
        excludeIf: isPlanBuilder,
      },
      { label: 'Launch templates', message: 'What templates do I need for an upcoming product launch?' },
      { label: 'Broken links', message: 'How do I check my templates for outdated copy or broken links?' },
    ],
  },

  // ── Design Studio — WhatsApp editor (before general DS — first match wins) ──
  {
    routes: ['/workspaces/:workspaceId/design-studio/editor/whatsapp/*'],
    suggestions: [],
  },

  // ── Design Studio ─────────────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/design-studio/dashboard',
      '/workspaces/:workspaceId/design-studio/editor/*',
      '/workspaces/:workspaceId/design-studio/welcome',
      '/workspaces/:workspaceId/design-studio/global-styles',
    ],
    suggestions: [
      { label: 'Spellcheck', message: 'Check my email for spelling and grammar mistakes and fix any issues you find.' },
      { label: 'Improve subject line', message: 'How do I write a compelling email subject line and preview text?' },
      { label: 'Rewrite for clarity', message: 'How do I make email copy more concise and action-oriented?' },
      { label: 'A/B test version', message: 'How do I write an alternative version of an email for an A/B test?' },
      {
        label: 'Write a strong CTA',
        message: 'What are best practices for writing effective call-to-action buttons in emails?',
      },
      {
        label: 'Conditional content',
        message: 'How do I add conditional content blocks that show different content based on user attributes?',
      },
    ],
  },

  // ── Transactional Messages ────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/transactional/*'],
    suggestions: [
      {
        label: 'Password reset email',
        message: 'How do I write a clear, on-brand password reset email that reduces friction?',
      },
      {
        label: 'Personalization variables',
        message: 'What personalization variables should I include in transactional messages?',
      },
      {
        label: 'Fallback values',
        message: 'How do I add a safe fallback for when a personalization variable is missing?',
      },
      {
        label: 'Test without sending',
        message: 'How do I test a transactional message without sending it to a real customer?',
      },
      {
        label: 'Transactional vs marketing',
        message: 'What is the difference between transactional and marketing messages?',
      },
    ],
  },

  // ── Analysis / Metrics ────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/analysis', '/workspaces/:workspaceId/analysis/deliverability'],
    suggestions: [
      {
        label: 'Deliverability health',
        message: 'What does good email deliverability look like and what are red flags to watch for?',
      },
      { label: 'High bounce rate', message: 'My bounce rate looks high — what does that mean and what should I do?' },
      {
        label: 'Best performing campaigns',
        message: 'How do I identify which campaigns are performing best and why?',
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Engagement by segment',
        message: 'How do I compare engagement rates across different audience segments?',
        excludeIf: isPlanBuilder,
      },
      { label: 'Weekly summary', message: 'How do I build a weekly performance summary to share with my team?' },
    ],
  },

  // ── Data & Integrations ───────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/data-index/*',
      '/workspaces/:workspaceId/journeys/integrations/*',
      '/workspaces/:workspaceId/journeys/imports/*',
      '/workspaces/:workspaceId/journeys/exports/*',
      '/workspaces/:workspaceId/journeys/collections/*',
    ],
    suggestions: [
      {
        label: 'Unused events',
        message: 'How do I find tracked events that are not being used in any campaign trigger?',
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Event schema design',
        message: 'Help me design an event schema for tracking steps in an onboarding flow',
      },
      {
        label: 'Churn prevention data',
        message: 'What data do I need to power a churn prevention campaign?',
        excludeIf: isPlanBuilder,
      },
      {
        label: 'Verify event data',
        message: 'How do I verify that event data is arriving correctly and in the right format?',
      },
      {
        label: 'Stale events',
        message: 'How can I identify tracked events that have not fired recently and might be broken?',
      },
    ],
  },

  // ── Settings ──────────────────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/settings/*',
      '/workspaces/:workspaceId/settings/ai-business-profile',
      '/workspaces/:workspaceId/settings/audit-logs',
      '/settings/audit-logs',
      '/settings/account/*',
      '/settings/team/*',
      '/settings/billing/*',
      '/settings/security/*',
      '/settings/personal/*',
      '/settings/roles/*',
      '/settings/privacy',
      '/settings/workspaces/*',
      '/settings/subscription',
      '/settings/receipts',
      '/settings/api_credentials',
      '/settings/experimental-features',
    ],
    suggestions: [
      {
        label: 'Sending domain setup',
        message: 'What sending domain settings should I configure to maximize deliverability?',
      },
      {
        label: 'Custom unsubscribe page',
        message: 'How do I set up a custom unsubscribe page that matches our brand?',
      },
      {
        label: 'Team permissions',
        message: 'How do I add a team member and which permission level should I give them?',
      },
      {
        label: 'Subscription topics',
        message: 'What is the difference between subscription topics and a global unsubscribe?',
      },
      { label: 'GDPR compliance', message: 'What do I need to configure in my workspace to be GDPR-compliant?' },
    ],
  },

  // ── Goals — Milestones (before detail — /milestones is more specific) ─
  {
    routes: ['/workspaces/:workspaceId/journeys/goals/:goalId/milestones'],
    suggestions: [
      {
        label: 'What are milestones?',
        message: 'What is a milestone in goals and how does the milestone-to-goal funnel view work?',
      },
      {
        label: 'High drop-off rate',
        message:
          "What are common reasons people complete a milestone but don't go on to achieve the goal, and how do I address them?",
      },
      {
        label: 'Pick a good milestone',
        message: 'How do I choose the right milestone event — what behaviors tend to predict goal achievement?',
      },
      {
        label: 'Improve funnel conversion',
        message:
          'What are proven strategies to improve conversion between a milestone and a goal in a lifecycle marketing flow?',
      },
      {
        label: 'Milestone vs goal event',
        message:
          "What's the difference between a milestone event and a goal event, and can the same event be used as both?",
      },
    ],
  },

  // ── Goals — New (before detail — /new matches :goalId) ─────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/goals/new'],
    suggestions: [
      {
        label: 'Help me configure this',
        message: 'Walk me through the key decisions I need to make when creating a new goal in Customer.io.',
      },
      {
        label: 'Event or segment?',
        message: "What's the difference between an event-based and segment-based goal, and when should I use each?",
      },
      {
        label: 'Track revenue impact',
        message:
          'How do I configure a goal to track revenue impact, and when does tracking revenue value matter versus just tracking volume?',
      },
      {
        label: 'How does impact work?',
        message: 'What is impact value in goals, how is it calculated, and when should I bother tracking it?',
      },
      {
        label: 'Am I on the right track?',
        message:
          "I'm setting up a new goal — how do I validate that my goal event, impact settings, and milestone configuration make sense before saving?",
      },
    ],
  },

  // ── Goals — Detail / Overview ──────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/goals/:goalId/*'],
    suggestions: [
      {
        label: 'How does attribution work?',
        message:
          'How does attribution work in goals — how does Customer.io decide which campaign gets credit for a goal completion?',
      },
      {
        label: 'Unattributed completions?',
        message: 'What does it mean when I see unattributed goal completions, and what should I do with that insight?',
      },
      {
        label: 'Improve completion rate',
        message:
          'What are the most effective ways to increase goal completion rate using campaigns and broadcasts in Customer.io?',
      },
      {
        label: 'Add more sources',
        message: 'How do I decide which campaigns and broadcasts to connect to a goal as sources?',
      },
      {
        label: 'Impact vs completions',
        message: "What's the difference between goal completions and impact value — when does each matter more?",
      },
    ],
  },

  // ── Goals — List ───────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/goals'],
    suggestions: [
      {
        label: 'What are goals?',
        message: 'What are goals in Customer.io and how is it different from setting a goal inside a campaign?',
      },
      {
        label: 'Why use goals?',
        message: "What problem do goals solve that I can't solve with campaign-level conversion tracking?",
      },
      {
        label: 'What makes a good goal?',
        message:
          'What are the characteristics of a well-defined goal — how do I know if something is a good candidate?',
      },
      {
        label: 'Suggest a goal',
        message:
          'What are some good goals I should track based on common lifecycle marketing use cases like activation, retention, or re-engagement?',
      },
      {
        label: 'Goals best practices',
        message: 'What are best practices for organizing and naming goals in Customer.io?',
      },
    ],
  },

  // ── Pipelines (CDP) ───────────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/pipelines/dashboard',
      '/workspaces/:workspaceId/pipelines/setup-guide/*',
      '/workspaces/:workspaceId/pipelines/sources/*',
      '/workspaces/:workspaceId/pipelines/destinations/*',
      '/workspaces/:workspaceId/pipelines/connections',
      '/workspaces/:workspaceId/pipelines/catalog',
    ],
    suggestions: [
      { label: 'What are pipelines?', message: 'What is Customer.io Data Pipelines and how does it work?' },
      { label: 'Add a source', message: 'How do I add a new data source in Data Pipelines?' },
      { label: 'Add a destination', message: 'How do I connect a destination to send data to?' },
      {
        label: 'Source vs destination',
        message: 'What is the difference between a source and a destination in Data Pipelines?',
      },
      { label: 'Reverse ETL', message: 'What is reverse ETL and how do I set it up in Customer.io?' },
    ],
  },
];

const MARQUEE_ROW_COUNT = 3;
/** Number of route-specific suggestions to mix into the marquee when a route matches */
const ROUTE_SPECIFIC_COUNT = 8;
/** Number of generic suggestions to mix in alongside route-specific ones */
const GENERIC_MIX_COUNT = 12;

/**
 * Get 3 random static suggestions for initial page load (used in next-message suggestions).
 * When a pathname is provided, returns route-specific suggestions if available.
 */
export function getInitialStaticSuggestions(pathname?: string, options: { isBuilder?: boolean } = {}) {
  const context: SuggestionContext = { isBuilder: options.isBuilder === true };
  const filterByPlan = (suggestions: Suggestion[]): Suggestion[] =>
    suggestions.filter((s) => s.excludeIf == null || !s.excludeIf(context));
  // Filter before deciding which pool to draw from, so a route group whose
  // suggestions are all plan-excluded falls back to STATIC_SUGGESTIONS instead
  // of returning fewer than the intended count.
  const routeSuggestions = pathname != null ? filterByPlan(getRouteSuggestions(pathname)) : [];
  const pool = routeSuggestions.length > 0 ? routeSuggestions : filterByPlan(STATIC_SUGGESTIONS);
  const shuffled = pool.toSorted(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
}

export const HUMAN_SUPPORT_SUGGESTION = {
  label: 'Human support',
  message: 'Talk to a human support agent',
} as const;

export interface IndexedSuggestion {
  index: number;
  label: string;
  message: string;
}

/**
 * Get route-specific suggestions for the current pathname.
 * Checks each group in order and returns the first match.
 */
function getRouteSuggestions(pathname: string): Suggestion[] {
  for (const group of PAGE_SUGGESTIONS) {
    if (matchRoutes(pathname, group.routes)) {
      return group.suggestions;
    }
  }
  return [];
}

/**
 * Get static suggestions distributed into rows for the marquee animation.
 * Each suggestion gets a unique index for analytics tracking.
 *
 * When a pathname is provided and matches a route group:
 * - Mixes route-specific suggestions with a subset of generic ones
 * - Route-specific suggestions are prioritized
 *
 * When no pathname is provided or no route matches:
 * - Falls back to the full set of generic suggestions
 */
export function getMarqueeSuggestionRows({
  shouldIncludeHumanSupport = false,
  pathname,
  isBuilder = false,
}: {
  shouldIncludeHumanSupport?: boolean;
  pathname?: string;
  isBuilder?: boolean;
} = {}): IndexedSuggestion[][] {
  const context: SuggestionContext = { isBuilder };
  const filterByPlan = (suggestions: Suggestion[]): Suggestion[] =>
    suggestions.filter((s) => s.excludeIf == null || !s.excludeIf(context));

  const routeSuggestions = filterByPlan(pathname != null ? getRouteSuggestions(pathname) : []);
  const staticSuggestions = filterByPlan(STATIC_SUGGESTIONS);

  let pool: Suggestion[];

  if (routeSuggestions.length > 0) {
    // Mix route-specific suggestions with a subset of generic ones
    const shuffledRoute = routeSuggestions.toSorted(() => Math.random() - 0.5);
    const routeLabels = new Set(shuffledRoute.map((s) => s.label));
    const genericPool = staticSuggestions.filter((s) => !routeLabels.has(s.label));
    const shuffledGeneric = genericPool.sort(() => Math.random() - 0.5);

    pool = [...shuffledRoute.slice(0, ROUTE_SPECIFIC_COUNT), ...shuffledGeneric.slice(0, GENERIC_MIX_COUNT)];
  } else {
    pool = [...staticSuggestions];
  }

  if (shouldIncludeHumanSupport) {
    pool.push(HUMAN_SUPPORT_SUGGESTION);
  }

  const shuffled = pool.sort(() => Math.random() - 0.5);
  const indexed = shuffled.map((s, index) => ({ ...s, index: index + 1 }));
  const rows: IndexedSuggestion[][] = [];
  const perRow = Math.ceil(indexed.length / MARQUEE_ROW_COUNT);

  for (let index = 0; index < MARQUEE_ROW_COUNT; index++) {
    rows.push(indexed.slice(index * perRow, (index + 1) * perRow));
  }

  return rows;
}
